import { openingLanguage, translationsFor } from "../i18n/language-provider";
import { ReloadOffer } from "./ReloadOffer";
import { SUPPORT_EMAIL } from "./support/support-contact";

/**
 * What stands in for the whole app when a render fault reaches the root.
 *
 * `main.tsx` mounts it as the fallback of the boundary above every provider,
 * the backstop under all the others: a fault in the shell itself (`App`, the
 * page router, the toaster, the discard dialog) or in a provider has nothing
 * else above it, and used to leave a blank window with no way back but a
 * refresh the user had to think of themselves.
 *
 * **It offers a reload and never takes one.** `staleChunkRecovery` reloads by
 * itself, once, because it knows its cause: a route's chunk is gone from
 * origin, so the tab is stale and a reload is the repair. A fault that reaches
 * here has no known cause. Where it is a page older than the server, the
 * reload fixes it, which is what the offer says. Where it is a bug in the page
 * itself the reload changes nothing, and a reload taken unasked would be a
 * loop for the user to watch. So the reload is the user's, the report is what
 * gets the bug fixed, and the address is named for when the reload does not
 * help: no rail is left to ask Optivo from. Nothing is tried again by itself
 * either, since everything under the root went with the fault, the state the
 * pages held included.
 *
 * **It reads nothing a provider gives.** It is drawn above the providers, or
 * with them gone, so it cannot ask `useLanguage()`: outside a provider that
 * answers English to everybody, and a Czech operator stuck on this screen
 * reads it on every reload. It asks the provider's own rule
 * (`openingLanguage`), and is handed the same `followBrowserLanguage` the
 * provider is, so the two open in one language. A storage that cannot be read
 * is one of the faults that end here, and reads as nobody having picked one.
 * The skin and the light or dark mode are attributes of `<html>`, which
 * outlive the providers that set them, so the tokens still paint.
 */

interface AppUnavailableProps {
    /** Open in the browser's language when nobody has picked one, as `LanguageProvider` is told to. */
    followBrowserLanguage?: boolean;
}

export function AppUnavailable({ followBrowserLanguage = false }: AppUnavailableProps) {
    const words = translationsFor(openingLanguage({ followBrowserLanguage })).app;
    const [beforeAddress, afterAddress = ""] = words.pageLoadFailedContact.split("{email}");

    return (
        <ReloadOffer
            fill="window"
            title={words.pageLoadFailedTitle}
            message={words.pageLoadFailedMessage}
            reloadLabel={words.pageLoadFailedReload}
            note={
                <>
                    {beforeAddress}
                    <a className="underline underline-offset-2" href={`mailto:${SUPPORT_EMAIL}`}>
                        {SUPPORT_EMAIL}
                    </a>
                    {afterAddress}
                </>
            }
        />
    );
}
